import { API_ENDPOINTS, API_CONFIG, PAGINATION_DEFAULTS } from '@/config/api';
import { authService } from './authService';
import {
  ComplianceRequirement
} from '@/types';
import {
  LookupTablesResponse,
  ScopeDto,
  DistrictDto,
  RequirementsByScopeResponse,
  PaginationParams,
  CreateRequirementApiPayload,
  UpdateRequirementApiPayload,
  EnableRequirementApiPayload,
  BulkUploadDocumentResult,
  RequirementDetailDto,
  BulkUpdateStatusRequest,
  BulkUpdateStatusResult
} from '@/types/complianceRequirements';
import { STATUS_VALUES } from '@/pages/ComplianceRequirements/constants';
import { ApiError } from './complianceService';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Extracts a user-friendly error message from various API error response structures
 * @param errorData - The parsed error response from the API
 * @param fallback - Fallback message if no error details can be extracted
 * @returns A string error message
 */
function extractFromErrorsField(errors: any): string | null {
  if (!errors) return null;

  if (Array.isArray(errors) && errors.length > 0) {
    const stringMessages = errors.filter((err: any) => typeof err === 'string');
    if (stringMessages.length > 0) return stringMessages.join('; ');

    const objectMessages = errors
      .map((err: any) => err?.message)
      .filter((msg: any) => typeof msg === 'string');
    if (objectMessages.length > 0) return objectMessages.join('; ');
  }

  if (typeof errors === 'object' && !Array.isArray(errors)) {
    const messages = [...new Set(
      Object.values(errors as Record<string, string[]>)
        .flat()
        .filter((msg): msg is string => typeof msg === 'string')
    )];
    if (messages.length > 0) return messages.join('; ');
  }

  return null;
}

function extractErrorMessage(errorData: any, fallback: string): string {
  if (!errorData) return fallback;

  if (typeof errorData.error === 'string' && errorData.error.trim()) {
    return errorData.error;
  }

  if (errorData.error?.message && typeof errorData.error.message === 'string') {
    return errorData.error.message;
  }

  if (errorData.message && typeof errorData.message === 'string') {
    return errorData.message;
  }

  const fromErrors = extractFromErrorsField(errorData.errors);
  if (fromErrors) return fromErrors;

  if (typeof errorData.title === 'string' && errorData.title.trim()) {
    return errorData.title;
  }

  return fallback;
}

/**
 * Handles API error responses consistently across all service methods
 * @param response - The failed HTTP response
 * @param action - Description of the action that failed (e.g., 'update compliance requirement')
 * @throws ApiError with extracted error message and details
 */
async function handleApiError(response: Response, action: string): Promise<never> {
  const errorData = await response.json().catch(() => null);
  const errorMessage = extractErrorMessage(errorData, response.statusText);
  throw new ApiError(
    `Failed to ${action}: ${errorMessage}`,
    response.status,
    errorData
  );
}

/**
 * Makes a JSON API request with consistent configuration
 * @param url - The API endpoint URL
 * @param options - Fetch options (method, body, etc.)
 * @param action - Description of the action for error messages
 * @returns The parsed JSON response
 */
async function fetchJson<T = any>(
  url: string,
  options: {
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    body?: any;
    headers?: Record<string, string>;
  },
  action: string
): Promise<T> {
  const { method, body, headers = {} } = options;

  const requestOptions: RequestInit = {
    method,
    signal: AbortSignal.timeout(API_CONFIG.timeout),
    headers: {
      ...(body !== undefined && { 'Content-Type': 'application/json' }),
      ...authService.getAuthHeader(),
      ...headers,
    },
  };

  if (body !== undefined) {
    requestOptions.body = JSON.stringify(body);
  }

  const response = await fetch(url, requestOptions);

  if (!response.ok) {
    await handleApiError(response, action);
  }

  return response.json();
}

// ============================================================================
// LOOKUP TABLES SERVICE
// ============================================================================

/**
 * CACHE STRATEGY:
 * - Time-based expiry (5 min default) for static reference data
 * - Optional mutation-based invalidation (enable via VITE_INVALIDATE_CACHE_ON_MUTATION=true)
 * - Manual invalidation via clearLookupTablesCache()
 *
 * Time-based expiry is sufficient because lookup tables contain static reference data
 * that changes infrequently through admin operations, not regular CRUD.
 */

// Cache for lookup tables
let lookupTablesCache: LookupTablesResponse | null = null;
let cacheTimestamp: number | null = null;
let inflightRequest: Promise<LookupTablesResponse> | null = null;

/**
 * Fetches all lookup tables (scopes, statuses, types, etc.)
 * @param forceRefresh - Bypass cache and fetch fresh data
 */
export const fetchLookupTables = async (forceRefresh = false): Promise<LookupTablesResponse> => {
  try {
    // Check cache
    if (!forceRefresh && lookupTablesCache && cacheTimestamp) {
      const now = Date.now();
      if (now - cacheTimestamp < API_CONFIG.cacheDuration.lookupTables) {
        return lookupTablesCache;
      }
    }

    // If a request is already in flight, handle based on forceRefresh parameter
    if (inflightRequest) {
      if (forceRefresh) {
        // Wait for the current inflight request to finish, then fetch fresh data
        await inflightRequest;
        // After the inflight request completes, proceed to fetch fresh data (skip cache)
      } else {
        // Return the inflight request to avoid duplicate requests
        return inflightRequest;
      }
    }

    // Create and store the inflight request promise
    inflightRequest = (async () => {
      try {
        const response = await fetch(API_ENDPOINTS.lookupTables(), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          signal: AbortSignal.timeout(API_CONFIG.timeout),
        });

        if (!response.ok) {
          await handleApiError(response, 'fetch lookup tables');
        }

        const data = await response.json();

        // Update cache
        lookupTablesCache = data;
        cacheTimestamp = Date.now();

        return data;
      } finally {
        // Clear the inflight request after completion (success or failure)
        inflightRequest = null;
      }
    })();

    return inflightRequest;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    if (error instanceof Error) {
      throw new ApiError(
        error.message || 'Failed to fetch lookup tables',
        undefined,
        error
      );
    }
    throw new ApiError('An unknown error occurred');
  }
};

/**
 * Fetches scopes from the dedicated Scopes API endpoint
 */
export const fetchScopes = async (): Promise<ScopeDto[]> => {
  try {
    const response = await fetch(API_ENDPOINTS.scopes(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: AbortSignal.timeout(API_CONFIG.timeout),
    });

    if (!response.ok) {
      await handleApiError(response, 'fetch scopes');
    }

    const data: ScopeDto[] = await response.json();
    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    if (error instanceof Error) {
      throw new ApiError(
        error.message || 'Failed to fetch scopes',
        undefined,
        error
      );
    }
    throw new ApiError('An unknown error occurred');
  }
};

/**
 * Fetches school districts from the dedicated Districts API endpoint
 */
export const fetchSchoolDistricts = async (): Promise<DistrictDto[]> => {
  try {
    const response = await fetch(API_ENDPOINTS.districts.schoolDistricts(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: AbortSignal.timeout(API_CONFIG.timeout),
    });

    if (!response.ok) {
      await handleApiError(response, 'fetch school districts');
    }

    const data: DistrictDto[] = await response.json();
    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    if (error instanceof Error) {
      throw new ApiError(
        error.message || 'Failed to fetch school districts',
        undefined,
        error
      );
    }
    throw new ApiError('An unknown error occurred');
  }
};

/**
 * Fetches states from the dedicated Districts API endpoint
 */
export const fetchStates = async (): Promise<DistrictDto[]> => {
  try {
    const response = await fetch(API_ENDPOINTS.districts.states(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: AbortSignal.timeout(API_CONFIG.timeout),
    });

    if (!response.ok) {
      await handleApiError(response, 'fetch states');
    }

    const data: DistrictDto[] = await response.json();
    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    if (error instanceof Error) {
      throw new ApiError(
        error.message || 'Failed to fetch states',
        undefined,
        error
      );
    }
    throw new ApiError('An unknown error occurred');
  }
};

/**
 * Clears the lookup tables cache
 *
 * USE CASES:
 * - Manual cache invalidation after admin operations that modify lookup tables
 * - Testing and development scenarios
 *
 * WHEN TO CALL THIS FUNCTION:
 * Call this function after any admin operation that modifies lookup table data:
 * - Adding, editing, or deleting a Scope (via the Scopes admin API)
 * - Adding, editing, or deleting a Requirement Type
 * - Adding, editing, or deleting a Requirement Status
 * - Any other admin action that changes the reference data returned by fetchLookupTables
 *
 * NOTE: If VITE_INVALIDATE_CACHE_ON_MUTATION is enabled, cache will be automatically
 * invalidated after requirement mutations. However, for admin operations on lookup
 * tables themselves, you should call this function explicitly.
 *
 * @example
 * // After creating a new scope via admin API
 * await createScope(newScopeData);
 * clearLookupTablesCache();
 */
export const clearLookupTablesCache = (): void => {
  lookupTablesCache = null;
  cacheTimestamp = null;
  inflightRequest = null;
};

/**
 * Conditionally invalidates cache based on configuration
 *
 * This function is called automatically after requirement mutations (create, update, delete, etc.).
 * Only invalidates the cache if VITE_INVALIDATE_CACHE_ON_MUTATION is enabled.
 *
 * NOTE: This is primarily for development/testing. In production, lookup tables are typically
 * not modified by requirement operations, so time-based expiry is usually sufficient.
 *
 * @internal
 */
const invalidateCacheIfEnabled = (): void => {
  if (API_CONFIG.cacheInvalidation.invalidateOnMutation) {
    clearLookupTablesCache();
  }
};

// ============================================================================
// COMPLIANCE REQUIREMENTS SERVICE
// ============================================================================

/**
 * Creates a new compliance requirement
 * @param input - Requirement data with IDs from lookup tables
 * @returns Promise with created requirement
 */
export const createComplianceRequirement = async (
  input: CreateRequirementApiPayload
): Promise<ComplianceRequirement> => {
  try {
    const result = await fetchJson<{ success: boolean; data: string }>(
      API_ENDPOINTS.complianceRequirements.create(),
      { method: 'POST', body: input },
      'create compliance requirement'
    );

    // Invalidate cache if configured to do so
    invalidateCacheIfEnabled();

    // API returns { success: true, data: "requirement-id" }
    // Return minimal object with ID - full data will be fetched via get-by-scope
    return {
      id: result.data,
    } as ComplianceRequirement;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    if (error instanceof Error) {
      throw new ApiError(
        error.message || 'Failed to create compliance requirement',
        undefined,
        error
      );
    }
    throw new ApiError('An unknown error occurred');
  }
};

/**
 * Saves a compliance requirement as draft
 * @param payload - API payload with requirement data (transformed with IDs)
 * @param lookupTables - Lookup tables to get Draft status ID
 * @returns Promise with created draft requirement
 */
export const saveDraftRequirement = async (
  payload: CreateRequirementApiPayload,
  lookupTables: LookupTablesResponse
): Promise<ComplianceRequirement> => {
  try {
    // Find Draft status ID from lookup tables
    const draftStatus = lookupTables.statuses.find(
      s => s.statusName === STATUS_VALUES.DRAFT
    );

    if (!draftStatus) {
      throw new ApiError(`${STATUS_VALUES.DRAFT} status not found in lookup tables`);
    }

    // Override statusId to Draft
    const draftPayload: CreateRequirementApiPayload = {
      ...payload,
      statusId: draftStatus.id,
    };

    const result = await fetchJson<{ success: boolean; data: string }>(
      API_ENDPOINTS.complianceRequirements.create(),
      { method: 'POST', body: draftPayload },
      'save draft requirement'
    );

    // Invalidate cache if configured to do so
    invalidateCacheIfEnabled();

    // API returns { success: true, data: "requirement-id" }
    // Return minimal object with ID - full data will be fetched via get-by-scope
    return {
      id: result.data,
    } as ComplianceRequirement;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    if (error instanceof Error) {
      throw new ApiError(
        error.message || 'Failed to save draft requirement',
        undefined,
        error
      );
    }
    throw new ApiError('An unknown error occurred');
  }
};

/**
 * Updates an existing compliance requirement
 * @param input - Updated requirement data with IDs from lookup tables (must include requirementId)
 * @returns Promise with updated requirement
 */
export const updateComplianceRequirement = async (
  input: UpdateRequirementApiPayload
): Promise<ComplianceRequirement> => {
  try {
    const data = await fetchJson<any>(
      API_ENDPOINTS.complianceRequirements.update(),
      { method: 'PUT', body: input },
      'update compliance requirement'
    );

    // Invalidate cache if configured to do so
    invalidateCacheIfEnabled();

    return {
      ...data,
      createdAt: data.createdAt != null ? new Date(data.createdAt) : undefined,
      updatedAt: data.updatedAt != null ? new Date(data.updatedAt) : undefined,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    if (error instanceof Error) {
      throw new ApiError(
        error.message || 'Failed to update compliance requirement',
        undefined,
        error
      );
    }
    throw new ApiError('An unknown error occurred');
  }
};

/**
 * Updates the status of one or more compliance requirements
 * @param request - Update request with requirement IDs and target status ID
 * @returns Promise with update result containing successes and failures
 */
export const updateStatus = async (
  request: BulkUpdateStatusRequest
): Promise<BulkUpdateStatusResult> => {
  try {
    const result = await fetchJson<{ isSuccess: boolean; data: BulkUpdateStatusResult; errors?: string[] }>(
      API_ENDPOINTS.complianceRequirements.updateStatus(),
      { method: 'POST', body: request },
      'update requirement statuses'
    );

    // API returns { isSuccess: true, data: BulkUpdateStatusResult, errors: [] }
    if (!result.isSuccess || !result.data) {
      throw new ApiError(
        result.errors?.join(', ') || 'Failed to update requirement statuses'
      );
    }

    // Invalidate cache only after successful operation
    invalidateCacheIfEnabled();

    return result.data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    if (error instanceof Error) {
      throw new ApiError(
        error.message || 'Failed to update requirement statuses',
        undefined,
        error
      );
    }
    throw new ApiError('An unknown error occurred');
  }
};

/**
 * Enables a requirement by setting completion date and notification settings
 * @param request - Enable requirement payload
 * @returns void on success
 * @throws ApiError if the request fails
 */
export const enableRequirement = async (
  request: EnableRequirementApiPayload
): Promise<void> => {
  try {
    const result = await fetchJson<{ isSuccess: boolean; data?: any; errors?: string[] }>(
      API_ENDPOINTS.complianceRequirements.enable(),
      { method: 'POST', body: request },
      'enable requirement'
    );

    // API returns { isSuccess: true, data: any, errors: [] }
    if (!result.isSuccess) {
      throw new ApiError(
        result.errors?.join(', ') || 'Failed to enable requirement'
      );
    }

    // Invalidate cache only after successful operation
    invalidateCacheIfEnabled();
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    if (error instanceof Error) {
      throw new ApiError(
        error.message || 'Failed to enable requirement',
        undefined,
        error
      );
    }
    throw new ApiError('An unknown error occurred while enabling requirement');
  }
};

/**
 * Hard-deletes a compliance requirement and all its related rows.
 * Backend enforces that only requirements with status "Draft" can be deleted.
 * @param id - Requirement ID to delete
 * @throws ApiError with the backend's message if the requirement is not Draft, not found, or unauthorized
 */
export const deleteComplianceRequirement = async (id: string): Promise<void> => {
  if (!id) {
    throw new ApiError('Requirement ID is required');
  }

  try {
    const response = await fetch(API_ENDPOINTS.complianceRequirements.delete(id), {
      method: 'DELETE',
      headers: {
        ...authService.getAuthHeader(),
      },
      signal: AbortSignal.timeout(API_CONFIG.timeout),
    });

    if (!response.ok) {
      await handleApiError(response, 'delete compliance requirement');
    }

    invalidateCacheIfEnabled();
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    if (error instanceof Error) {
      throw new ApiError(
        error.message || 'Failed to delete compliance requirement',
        undefined,
        error,
      );
    }
    throw new ApiError('An unknown error occurred while deleting the requirement');
  }
};

/**
 * Copies an existing compliance requirement
 * Creates a new requirement with Draft status based on the source requirement
 * @param id - Source requirement ID to copy
 * @returns Promise with the new requirement ID
 */
export const copyRequirement = async (id: string): Promise<string> => {
  try {
    const result = await fetchJson<{ isSuccess: boolean; data: string; errors?: string[] }>(
      API_ENDPOINTS.complianceRequirements.copy(id),
      { method: 'POST' },
      'copy requirement'
    );

    // Invalidate cache if configured to do so
    invalidateCacheIfEnabled();

    // API returns { isSuccess: true, data: "new-requirement-id", errors: [] }
    if (!result.isSuccess || !result.data) {
      throw new ApiError(
        result.errors?.join(', ') || 'Failed to copy requirement'
      );
    }

    return result.data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    if (error instanceof Error) {
      throw new ApiError(
        error.message || 'Failed to copy requirement',
        undefined,
        error
      );
    }
    throw new ApiError('An unknown error occurred');
  }
};

/**
 * Fetches requirements by scope from the backend API
 * @param scopeName - Scope name (EverDriven, States, Districts)
 * @param pagination - Optional pagination parameters
 * @returns Promise with paginated requirements response
 */
export async function fetchRequirementsByScope(
  scopeName: string,
  pagination?: PaginationParams
): Promise<RequirementsByScopeResponse> {
  try {
    const params: PaginationParams = {
      pageNumber: pagination?.pageNumber ?? PAGINATION_DEFAULTS.pageNumber,
      pageSize: pagination?.pageSize ?? PAGINATION_DEFAULTS.pageSize,
      returnAll: pagination?.returnAll ?? false,
      archivedOnly: pagination?.archivedOnly ?? false,
      ...(pagination?.searchString ? { searchString: pagination.searchString } : {}),
      ...(pagination?.districtIds?.length ? { districtIds: pagination.districtIds } : {}),
      ...(pagination?.appliesTo?.length ? { appliesTo: pagination.appliesTo } : {}),
      ...(pagination?.requirementTypes?.length ? { requirementTypes: pagination.requirementTypes } : {}),
      ...(typeof pagination?.impacting === 'boolean' ? { impacting: pagination.impacting } : {}),
      ...(typeof pagination?.isShared === 'boolean' ? { isShared: pagination.isShared } : {}),
      ...(pagination?.statuses?.length ? { statuses: pagination.statuses } : {}),
    };

    const response = await fetch(
      API_ENDPOINTS.requirementsByScope(),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ scope: scopeName, ...params }),
        signal: AbortSignal.timeout(API_CONFIG.timeout),
      }
    );

    if (!response.ok) {
      // Handle 404 as a "no requirements found" case instead of an error
      if (response.status === 404) {
        // Return empty response structure
        return {
          groups: [],
          pageNumber: params.pageNumber ?? PAGINATION_DEFAULTS.pageNumber,
          pageSize: params.pageSize ?? PAGINATION_DEFAULTS.pageSize,
          totalCount: 0,
          totalPages: 0,
          hasPreviousPage: false,
          hasNextPage: false,
        };
      }

      await handleApiError(response, 'fetch requirements by scope');
    }

    const data: RequirementsByScopeResponse = await response.json();
    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    if (error instanceof Error) {
      throw new ApiError(
        error.message || 'Failed to fetch requirements by scope',
        undefined,
        error
      );
    }
    throw new ApiError('An unknown error occurred');
  }
}

/**
 * Fetches detailed requirement data by ID
 * @param id - The unique identifier of the requirement
 * @returns Promise with detailed requirement data including type-specific fields
 */
export async function fetchRequirementById(id: string): Promise<RequirementDetailDto> {
  try {
    const response = await fetch(
      API_ENDPOINTS.complianceRequirements.get(id),
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(API_CONFIG.timeout),
      }
    );

    if (!response.ok) {
      await handleApiError(response, 'fetch requirement by ID');
    }

    const result = await response.json();
    return result.data;  // Extract data from API response wrapper
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    if (error instanceof Error) {
      throw new ApiError(
        error.message || 'Failed to fetch requirement by ID',
        undefined,
        error
      );
    }
    throw new ApiError('An unknown error occurred');
  }
}

// ============================================================================
// DOCUMENT UPLOAD SERVICE
// ============================================================================

/**
 * Uploads multiple document files to a requirement in one request.
 * POST /requirements/documents/bulk-upload
 */
export const bulkUploadRequirementDocuments = async (
  requirementId: string,
  files: File[]
): Promise<BulkUploadDocumentResult> => {
  if (!requirementId) throw new ApiError('Requirement ID is required');
  if (files.length === 0) throw new ApiError('At least one file is required');

  try {
    const formData = new FormData();
    formData.append('RequirementId', requirementId);
    files.forEach(f => formData.append('Files', f));

    const response = await fetch(API_ENDPOINTS.complianceRequirements.bulkUploadDocuments(), {
      method: 'POST',
      body: formData,
      signal: AbortSignal.timeout(API_CONFIG.timeout),
    });

    if (!response.ok) await handleApiError(response, 'bulk upload documents');
    return response.json() as Promise<BulkUploadDocumentResult>;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    if (error instanceof Error) throw new ApiError(error.message || 'Failed to bulk upload documents', undefined, error);
    throw new ApiError('An unknown error occurred during bulk upload');
  }
};

/**
 * Soft-deletes (deactivates) one or more requirement documents.
 * DELETE /requirements/documents
 */
export const deactivateRequirementDocuments = async (documentIds: string[]): Promise<void> => {
  if (documentIds.length === 0) throw new ApiError('At least one document ID is required');

  try {
    const response = await fetch(API_ENDPOINTS.complianceRequirements.deleteDocuments(), {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ documentIds }),
      signal: AbortSignal.timeout(API_CONFIG.timeout),
    });

    if (!response.ok) await handleApiError(response, 'deactivate documents');
  } catch (error) {
    if (error instanceof ApiError) throw error;
    if (error instanceof Error) throw new ApiError(error.message || 'Failed to deactivate documents', undefined, error);
    throw new ApiError('An unknown error occurred during document deactivation');
  }
};

